import { useEffect, useRef, useState, type JSX } from 'react';
import { signInWithEmail, useUser } from '@taki/auth';
import type { TakiConfig } from '@taki/runtime-config';
import { LoginScreen } from '@taki/shell';
import { Button, IconAI } from '@taki/ui';

function oauthEndpoint(config: TakiConfig): string {
  const emulator = config.emulators?.functions;
  return emulator === undefined
    ? `${window.location.origin}/oauth/approve`
    : `http://${emulator.host}:${emulator.port}/${config.firebaseConfig.projectId}/europe-west1/iaOAuth/approve`;
}

/** Consentement OAuth : le membre utilise sa session Firebase, jamais une clef collee dans un client MCP. */
export function McpAuthorize({ config }: { config: TakiConfig }): JSX.Element {
  const { user, claims, loading } = useUser();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientName, setClientName] = useState('Assistant externe');
  const localLoginStarted = useRef(false);
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    const clientId = params.get('client_id');
    if (clientId === null) return;
    const url = new URL(oauthEndpoint(config).replace(/\/approve$/, '/client'));
    url.searchParams.set('client_id', clientId);
    void fetch(url).then(async (response) => {
      if (!response.ok) throw new Error('Client MCP inconnu.');
      const result: unknown = await response.json();
      if (typeof result !== 'object' || result === null || !('clientName' in result) ||
          typeof result.clientName !== 'string') throw new Error('Client MCP inconnu.');
      setClientName(result.clientName);
    }).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)));
  }, [config]);

  useEffect(() => {
    const local = config.emulators?.connexion;
    if (loading || user !== null || local === undefined || local.auto === false || localLoginStarted.current) return;
    localLoginStarted.current = true;
    void signInWithEmail(local.email, local.password).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : String(cause));
    });
  }, [config.emulators?.connexion, loading, user]);

  const authorize = async (): Promise<void> => {
    if (user === null || claims.orgId !== config.orgId) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(oauthEndpoint(config), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
        body: JSON.stringify({
          client_id: params.get('client_id'),
          redirect_uri: params.get('redirect_uri'),
          code_challenge: params.get('code_challenge'),
          code_challenge_method: params.get('code_challenge_method'),
          state: params.get('state'),
          resource: params.get('resource'),
        }),
      });
      const result: unknown = await response.json();
      if (!response.ok || typeof result !== 'object' || result === null ||
          !('redirectTo' in result) || typeof result.redirectTo !== 'string') {
        // Le serveur dit pourquoi (IA fermee, compte retire...) : le membre doit le lire.
        const reason = typeof result === 'object' && result !== null && 'error_description' in result &&
          typeof result.error_description === 'string' ? ` ${result.error_description}` : '';
        throw new Error(`Cette connexion n’a pas pu être autorisée.${reason}`);
      }
      window.location.assign(result.redirectTo);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  if (!loading && user === null) return <LoginScreen config={config} />;

  return <main className="flex min-h-full items-center justify-center bg-surface px-5 py-10 text-ink-900">
    <section className="w-full max-w-[30rem] rounded-lg border border-border bg-surface px-7 py-8">
      <div className="flex items-center gap-2 text-sm font-semibold"><IconAI size={20} /> MyTaki</div>
      <h1 className="mt-7 text-2xl font-semibold">Connecter {clientName}</h1>
      <p className="mt-3 text-sm leading-6 text-ink-500">
        Cet assistant utilisera vos droits MyTaki pour lire et agir dans l’organisation {config.orgId}.
      </p>
      {loading ? <p className="mt-6 text-sm text-ink-500">Vérification de votre compte...</p>
        : user !== null && claims.orgId !== config.orgId ? <p className="mt-6 text-sm text-ink-500">Ce compte n’a pas accès à cette organisation.</p>
        : user !== null ? <div className="mt-6 flex items-center gap-3">
            <Button variant="primary" disabled={busy} onClick={() => void authorize()}>Autoriser la connexion</Button>
            <span className="text-xs text-ink-500">{user.email}</span>
          </div> : null}
      {error !== null ? <p role="alert" className="mt-4 text-sm text-danger-ink">{error}</p> : null}
    </section>
  </main>;
}
